export const ROW_H = 32
export const HEADER_TIER_H = 24
export const HEADER_H = 2 * HEADER_TIER_H
export const BAR_H = 18
export const SUMMARY_H = 8
export const MILESTONE_SIZE = 14
export const BASELINE_H = 6
export const LINK_GAP = 8
export const INDENT_PX = 16
export const RANGE_PAD_BEFORE = 7
export const RANGE_PAD_AFTER = 14
export const EMPTY_RANGE_PAD = 30
export const OVERSCAN_ROWS = 8
export const OVERSCAN_DAYS = 30
export const MAX_IMPORT_BYTES = 50 * 1024 * 1024
export const MAX_DURATION_DAYS = 10000
export const PANEL_WIDTH = 360
/** Matches the server's `validateLag` bound (`src/graphql/schedule/dependency.ts`) — keep both in sync. */
export const MAX_LAG_DAYS = 1000
/** Task 18 drag handles (spec B §9.6): resize handle width, link handle radius, and the gap between
 * a bar's right edge and its link handle. Shared by `TaskBar`/`MilestoneMarker` (rendering the
 * handle) and `useBarDrag`/`GanttTimeline` (computing the same geometry for the link-preview line
 * and target highlight), so both sides always agree on where the handle actually is. */
export const RESIZE_HANDLE_W = 6
export const LINK_HANDLE_R = 4
export const LINK_HANDLE_GAP = 4

export const ZOOM_LEVELS = ['day', 'week', 'month', 'quarter', 'project'] as const
export type ZoomLevel = (typeof ZOOM_LEVELS)[number]
export const PX_PER_DAY: Record<Exclude<ZoomLevel, 'project'>, number> = { day: 40, week: 14, month: 5, quarter: 2 }
export const MIN_PX_PER_DAY = 0.5
/** Freelo #32277855: width kept free after the last day of the range so the label drawn to the
 * right of the last bar/milestone (`TaskBar`/`MilestoneMarker`) is never cut off at the right edge
 * of the pane. The whole-project zoom fits `range + gutter` into the viewport; fixed zooms make the
 * gutter scrollable-to. Sized for a long milestone name at `text-xs`. */
export const TRAILING_LABEL_PX = 200

export type DependencyType = 'FS' | 'SS' | 'FF' | 'SF'
export const DEPENDENCY_TYPES: DependencyType[] = ['FS', 'SS', 'FF', 'SF']
export type ConstraintType = 'ASAP' | 'SNET' | 'SNLT' | 'FNET' | 'FNLT' | 'MSO' | 'MFO'
export const CONSTRAINT_TYPES: ConstraintType[] = ['ASAP', 'SNET', 'SNLT', 'FNET', 'FNLT', 'MSO', 'MFO']
export const DATED_CONSTRAINTS: ConstraintType[] = ['SNET', 'SNLT', 'FNET', 'FNLT', 'MSO', 'MFO']
export type ScheduleHealth = 'COMPLETED' | 'ON_TRACK' | 'AT_RISK' | 'DELAYED'
export type ScheduleStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED'

export type ColumnId = 'rowNumber' | 'wbs' | 'name' | 'owner' | 'duration' | 'start' | 'finish' | 'predecessors' | 'percent' | 'float' | 'health'
/** Freelo #32277865: free-text owner (e.g. a subcontractor), same cap as the server's MAX_OWNER_LENGTH. */
export const MAX_OWNER_LENGTH = 200
export const COLUMN_WIDTHS: Record<ColumnId, number> = {
  rowNumber: 44, wbs: 72, name: 240, owner: 140, duration: 64, start: 96, finish: 96, predecessors: 128, percent: 56, float: 56, health: 40,
}
/** Per-column floor for user resizing — narrow enough to stay usable, wide enough that a header
 * label doesn't clip. */
export const COLUMN_MIN_WIDTHS: Record<ColumnId, number> = {
  rowNumber: 32, wbs: 40, name: 96, owner: 80, duration: 44, start: 72, finish: 72, predecessors: 60, percent: 40, float: 40, health: 32,
}
/** Shared ceiling for user resizing (every column). */
export const COLUMN_MAX_WIDTH = 800
/** Auto-fit (double-click on a header edge): horizontal padding added to the measured text, and the cap. */
export const AUTOFIT_PADDING = 20
export const AUTOFIT_MAX = 600
/** Width of the grid header's trailing column-visibility gear cell (matches Tailwind `w-7`). */
export const GRID_GEAR_WIDTH = 28
export const ALWAYS_VISIBLE_COLUMNS: ColumnId[] = ['rowNumber', 'wbs', 'name']
export const DEFAULT_COLUMN_VISIBILITY: Record<ColumnId, boolean> = {
  rowNumber: true, wbs: true, name: true, owner: true, duration: true, start: true, finish: true, predecessors: true, percent: true, float: true, health: true,
}
export const STORAGE_KEY_GANTT = 'urkla.schedule.gantt'
export const STORAGE_KEY_COLLAPSED = (scheduleId: string) => `urkla.schedule.collapsed.${scheduleId}`
